import {
  AdsFilteringType,
  CompletionStatusEnum,
  PropertyKindEnum,
  TransactionTypeEnum,
} from '../../../types';
import type { FilterState } from './filterTypes';

interface CreatorFilter {
  id?: string;
  name?: string;
  kind?: string;
}

interface PrepareMoreFiltersParams {
  draft: FilterState;
  filteringObj: AdsFilteringType;
  isRent?: boolean;
  myAgency: CreatorFilter;
}

const bedsAndBathsPropertyKinds = new Set<PropertyKindEnum>([
  PropertyKindEnum.Apartment,
  PropertyKindEnum.Villa,
  PropertyKindEnum.CommercialVilla,
  PropertyKindEnum.Townhouse,
  PropertyKindEnum.Penthouse,
  PropertyKindEnum.HotelApartment,
]);

const furnitureExcludedPropertyKinds = new Set<PropertyKindEnum>([
  PropertyKindEnum.Warehouse,
  PropertyKindEnum.ResidentialLand,
  PropertyKindEnum.IndustrialLand,
  PropertyKindEnum.CommercialVilla,
]);

const floorPropertyKinds = new Set<PropertyKindEnum>([
  PropertyKindEnum.Apartment,
  PropertyKindEnum.HotelApartment,
  PropertyKindEnum.Floor,
  PropertyKindEnum.Office,
  PropertyKindEnum.Warehouse,
]);

const renovationExcludedPropertyKinds = new Set<PropertyKindEnum>([
  PropertyKindEnum.HotelApartment,
  PropertyKindEnum.Warehouse,
  PropertyKindEnum.ResidentialLand,
  PropertyKindEnum.IndustrialLand,
  PropertyKindEnum.CommercialLand,
  PropertyKindEnum.Shop,
]);

const availabilityExcludedPropertyKinds = new Set<PropertyKindEnum>([
  PropertyKindEnum.ResidentialLand,
  PropertyKindEnum.IndustrialLand,
  PropertyKindEnum.CommercialLand,
]);

const isApplicableToAnyPropertyKind = (
  propertyKinds: PropertyKindEnum[],
  isApplicable: (propertyKind: PropertyKindEnum) => boolean
) => !propertyKinds.length || propertyKinds.some(isApplicable);

export const getMoreFiltersVisibility = (draft: FilterState, isRent?: boolean) => {
  const propertyKinds = draft.propertyKind;
  const isDistress = draft.transactionType.includes(TransactionTypeEnum.Distress);
  const isResale = draft.transactionType.includes(TransactionTypeEnum.Resale);
  const isReady = draft.transactionType.includes(TransactionTypeEnum.Ready);
  const isOffPlan = isDistress && draft.completionStatus.includes(CompletionStatusEnum.Ready);
  const handoverVisible = isDistress || isResale;
  const bedsAndBathsVisible = isApplicableToAnyPropertyKind(propertyKinds, (propertyKind) =>
    bedsAndBathsPropertyKinds.has(propertyKind)
  );
  const furnishedVisible = isApplicableToAnyPropertyKind(
    propertyKinds,
    (propertyKind) => !furnitureExcludedPropertyKinds.has(propertyKind)
  );
  const floorVisible = isApplicableToAnyPropertyKind(propertyKinds, (propertyKind) =>
    floorPropertyKinds.has(propertyKind)
  );
  const renovationVisible =
    (isRent || isReady || (isDistress && isOffPlan)) &&
    isApplicableToAnyPropertyKind(
      propertyKinds,
      (propertyKind) => !renovationExcludedPropertyKinds.has(propertyKind)
    );
  const availabilityVisible =
    (isReady || (isDistress && isOffPlan)) &&
    isApplicableToAnyPropertyKind(
      propertyKinds,
      (propertyKind) => !availabilityExcludedPropertyKinds.has(propertyKind)
    );

  return {
    isDistress,
    handoverVisible,
    bedsAndBathsVisible,
    furnishedVisible,
    floorVisible,
    renovationVisible,
    availabilityVisible,
  };
};

export const prepareMoreFiltersForApply = ({
  draft,
  filteringObj,
  isRent,
  myAgency,
}: PrepareMoreFiltersParams): AdsFilteringType => {
  const visibility = getMoreFiltersVisibility(draft, isRent);
  const handoverYear = draft.multiHandover.length === 1 ? draft.multiHandover[0] : undefined;
  const isMyAgency = draft.isMyAgency;

  return {
    ...filteringObj,
    locationId: draft.locationId,
    transactionType: draft.transactionType,
    rentFrequency: draft.rentFrequency,
    completionStatus: visibility.isDistress ? draft.completionStatus : undefined,
    propertyKind: draft.propertyKind,
    areaMin: Number(draft.size.areaMin) || undefined,
    areaMax: Number(draft.size.areaMax) || undefined,
    beds: visibility.bedsAndBathsVisible ? draft.beds : undefined,
    priceMin: Number(draft.price.priceMin) || undefined,
    priceMax: Number(draft.price.priceMax) || undefined,
    downPayment: visibility.handoverVisible ? draft.downPayment : undefined,
    baths: visibility.bedsAndBathsVisible ? draft.baths : undefined,
    furniture: visibility.furnishedVisible ? draft.furniture : undefined,
    renovation: visibility.renovationVisible ? draft.renovation : undefined,
    postHandover: visibility.handoverVisible ? draft.postHandover ?? undefined : undefined,
    'handover.quarter':
      visibility.handoverVisible && draft.handover.quarter && handoverYear
        ? draft.handover.quarter
        : visibility.handoverVisible && handoverYear
          ? 'Q4'
          : undefined,
    'handover.year': handoverYear && visibility.handoverVisible ? handoverYear : undefined,
    floor: visibility.floorVisible ? draft.floor : undefined,
    multiHandover:
      draft.multiHandover.length > 1 && visibility.handoverVisible
        ? draft.multiHandover
        : undefined,
    occupancy: visibility.availabilityVisible ? draft.occupancy : undefined,
    'creator.id': isMyAgency ? myAgency.id : draft.creator.id || undefined,
    'creator.name': isMyAgency ? myAgency.name : draft.creator.name || undefined,
    'creator.kind': isMyAgency ? myAgency.kind : draft.creator.kind || undefined,
    keywords: draft.keywords,
    creator: !draft.creator.id
      ? undefined
      : {
          id: draft.creator.id,
          name: draft.creator.name,
          kind: draft.creator.kind,
        },
    handover:
      !handoverYear || !visibility.handoverVisible
        ? {
            quarter: undefined,
            year: undefined,
          }
        : {
            quarter: draft.handover.quarter,
            year: handoverYear,
          },
    isMyAgency: draft.isMyAgency || undefined,
    myFriends: draft.myFriends || undefined,
  };
};
